/**
 * Campaign requests: data and actions for the brand and admin pages.
 *
 * The API checks every rule again. The form uses the same rules (@morcast/campaigns) only to
 * show problems before the request is sent.
 *
 * Only the brand changes a request's terms, and the admin team reviews every change (D25): the
 * brand edits a request waiting for review, and asks for a change of an accepted or lapsed one,
 * which an admin accepts into a new manifest or rejects with a note.
 *
 * Until it is funded, a request can move to another wallet (D26): the brand offers it, and the
 * other wallet takes it over once signed in.
 */

import type {
  BrandAssetType,
  CampaignRequestInput,
  Platform,
  YoutubeFormat,
} from "@morcast/campaigns";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DEPLOYMENTS_QUERY_KEY, type Deployment } from "../admin/deployments.js";
import { api } from "../api/client.js";
import { awaitsRecording } from "./deposit.js";

/** Statuses of a campaign request (see the API README). */
export type RequestStatus =
  | "submitted"
  | "accepted"
  | "rejected"
  | "withdrawn"
  | "lapsed"
  | "funded";

/** An image of a brief's brand kit (D24): its name, its SHA-256 and its type. */
export interface BrandKitAsset {
  label: string;
  sha256: string;
  mediaType: BrandAssetType;
}

/** Uploads of brand kit images, which a request waits for before it is sent. */
const BRAND_IMAGE_UPLOAD = ["brand-image-upload"];

/** Uploads an image for a brand kit. Answers its SHA-256, type and size. */
export function useUploadBrandImage() {
  return useMutation({
    mutationKey: BRAND_IMAGE_UPLOAD,
    mutationFn: (file: File) =>
      api.upload<{ sha256: string; mediaType: BrandAssetType; bytes: number }>(
        "/api/brand-assets",
        file,
      ),
  });
}

/** Whether an image of the brand kit is still uploading. A request sent now would leave it out. */
export function useUploadingBrandImage(): boolean {
  return useIsMutating({ mutationKey: BRAND_IMAGE_UPLOAD }) > 0;
}

/** A campaign request, as the API describes it. */
export interface CampaignRequest {
  id: number;
  status: RequestStatus;
  brand: string;
  brandName: string;
  contactEmail: string;
  chainId: number;
  platform: Platform;
  format: YoutubeFormat | null;
  token: { address: string; symbol: string | null; decimals: number | null };
  /** Budget in base units, as a decimal string. */
  budget: string;
  target: string;
  /** Creator threshold `M`. */
  threshold: string;
  metric: { unit: string; description: string };
  startAt: string;
  endAt: string;
  submissionLimit: number;
  brief: {
    title: string;
    description: string;
    requiredLinks: string[];
    requiredHashtags: string[];
    requiredMentions: string[];
    requiredText: string[];
    disclosure: string;
    semanticRequirements: string[];
    visualRequirements: string[];
    prohibitedContent: string[];
    allowedEdits: string;
    /** The brand kit. Absent from manifests of briefs without one. */
    assets?: BrandKitAsset[];
  };
  createdAt: string;
  updatedAt: string;
  /** The admin team's decision. `by` is present for admins only. */
  decision: { at: string; note: string | null; by?: string | null } | null;
  /** The accepted terms' hash, once accepted. */
  manifestHash: string | null;
  /** What the brand's deposit must use, once accepted (see `Deposit`). */
  deposit: Deposit | null;
  /** The on-chain campaign the deposit created, once the request is funded. */
  campaign: Campaign | null;
  /** Deposits with the request's manifest hash that fund nothing, oldest first. */
  unmatchedDeposits: UnmatchedDeposit[];
  /** The brand's changes of the terms, newest first. At most one is pending. */
  changes: RequestChange[];
  /** The request's moves to other wallets, newest first. At most one is pending. */
  transfers: RequestTransfer[];
}

/** A move of a request to another wallet (D26), and what became of it. */
export interface RequestTransfer {
  id: number;
  status: "pending" | "accepted" | "declined" | "cancelled" | "superseded";
  /** The wallet the request belonged to when it was offered. */
  from: string;
  /** The wallet it was offered to. */
  to: string;
  createdAt: string;
  closedAt: string | null;
  /** The manifest given again with the new wallet, for an accepted request. */
  manifestHash: string | null;
}

/** The request's move waiting to be taken over, if any. */
export function pendingTransfer(
  request: Pick<CampaignRequest, "transfers">,
): RequestTransfer | undefined {
  return request.transfers.find((transfer) => transfer.status === "pending");
}

/** A change of a request's terms the brand asked for, and what became of it. */
export interface RequestChange {
  id: number;
  status: "pending" | "accepted" | "rejected" | "withdrawn" | "superseded";
  /** The terms asked for, in full, as the request form sends them. */
  terms: CampaignRequestInput;
  /** What changes and why, in the brand's words. */
  reason: string;
  createdAt: string;
  /** When it was decided, withdrawn or superseded. Null while pending. */
  closedAt: string | null;
  /** The admin team's note: optional for an acceptance, present for a rejection. */
  decisionNote: string | null;
  /** The admin who decided it. For admins only. */
  decidedBy?: string | null;
  /** The manifest that accepting the change created. */
  manifestHash: string | null;
}

/** The request's change waiting for review, if any. */
export function pendingChange(
  request: Pick<CampaignRequest, "changes">,
): RequestChange | undefined {
  return request.changes.find((change) => change.status === "pending");
}

/**
 * The parameters of the brand's `createCampaign` call, as the manifest fixes them. Amounts are
 * base units and times Unix seconds, as decimal strings.
 */
export interface Deposit {
  chainId: number;
  escrow: string;
  token: string;
  budget: string;
  target: string;
  startAt: string;
  endAt: string;
  manifestHash: string;
  /** ISO 8601: the escrow refuses deposits at or after the start. */
  depositBefore: string;
}

/** The on-chain campaign a deposit created, as Morcast recorded it from the chain. */
export interface Campaign {
  chainId: number;
  escrow: string;
  /** The escrow's ID of the campaign, as a decimal string. */
  id: string;
  /** As the escrow reports it. */
  status: "funded" | "cancelled" | "settled" | "refunded";
  createdTx: string;
  createdAt: string;
}

/**
 * A deposit that carries the request's manifest hash but funds no request: a second deposit, one
 * from another wallet or with other parameters, or one that arrived after the request was
 * withdrawn. Morcast voids it, which returns its budget to the wallet that sent it.
 */
export interface UnmatchedDeposit {
  chainId: number;
  escrow: string;
  /** The escrow's ID of the campaign the deposit created, as a decimal string. */
  id: string;
  /** The wallet that sent it, which a void pays back. */
  brand: string;
  /** The token it deposited, which may differ from the request's. */
  token: CampaignRequest["token"];
  /** In base units of that token, as a decimal string. */
  budget: string;
  /** As the escrow reports it: "funded" until it is voided ("refunded") or cancelled. */
  status: Campaign["status"];
  createdTx: string;
  createdAt: string;
  /** Why it funds no request, in Morcast's words. */
  reason: string;
}

/** Query keys of request data in the React Query cache. */
export const REQUESTS_KEY = ["campaign-requests"] as const;

/** How often a request is read again while a deposit this browser sent awaits recording. */
const RECORDING_POLL_MS = 4_000;

/**
 * The tokens brands can use now: those the deployment receiving new campaigns accepts. Empty
 * when no deployment receives new campaigns.
 */
export function useAcceptedTokens() {
  return useQuery({
    queryKey: DEPLOYMENTS_QUERY_KEY,
    queryFn: async () =>
      (await api.get<{ deployments: Deployment[] }>("/api/deployments")).deployments,
    select: (deployments) =>
      deployments.find((deployment) => deployment.acceptsNewCampaigns)?.tokens ?? [],
  });
}

/** The signed-in wallet's requests, and those other wallets offer it, as one answer. */
const myRequestsQuery = {
  queryKey: [...REQUESTS_KEY, "mine"],
  queryFn: () =>
    api.get<{ requests: CampaignRequest[]; offered: CampaignRequest[] }>("/api/campaign-requests"),
};

/** The signed-in brand's requests, newest first. */
export function useMyRequests() {
  return useQuery({ ...myRequestsQuery, select: (answer) => answer.requests });
}

/** The requests other wallets offer the signed-in wallet to take over (D26), newest first. */
export function useOfferedRequests() {
  return useQuery({ ...myRequestsQuery, select: (answer) => answer.offered ?? [] });
}

/**
 * Every request, optionally with one status, or only those with a change waiting for review
 * (admins).
 */
export function useAllRequests(filter: RequestStatus | "change" | undefined) {
  const query =
    filter === "change" ? "?change=pending" : filter === undefined ? "" : `?status=${filter}`;
  return useQuery({
    queryKey: [...REQUESTS_KEY, "all", filter ?? "any"],
    queryFn: async () =>
      (await api.get<{ requests: CampaignRequest[] }>(`/api/admin/campaign-requests${query}`))
        .requests,
  });
}

/**
 * One request. While a deposit this browser sent awaits recording, the request is read again
 * every few seconds, so the page shows the campaign as soon as Morcast has recorded it.
 */
export function useRequest(id: number, enabled = true) {
  return useQuery({
    queryKey: [...REQUESTS_KEY, "one", id],
    queryFn: () => api.get<CampaignRequest>(`/api/campaign-requests/${id}`),
    enabled,
    refetchInterval: (query) =>
      query.state.data && awaitsRecording(query.state.data) ? RECORDING_POLL_MS : false,
  });
}

/** An action on requests that refreshes every cached request list when it succeeds. */
function useRequestAction<Input>(action: (input: Input) => Promise<CampaignRequest>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: action,
    onSettled: () => queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }),
  });
}

/** Submits a new request. */
export function useSubmitRequest() {
  return useRequestAction((input: CampaignRequestInput) =>
    api.post<CampaignRequest>("/api/campaign-requests", input),
  );
}

/** Withdraws the brand's request. */
export function useWithdrawRequest() {
  return useRequestAction((id: number) =>
    api.post<CampaignRequest>(`/api/campaign-requests/${id}/withdraw`),
  );
}

/** Replaces the terms of the brand's request waiting for review. */
export function useEditRequest() {
  return useRequestAction((input: { id: number; terms: CampaignRequestInput }) =>
    api.put<CampaignRequest>(`/api/campaign-requests/${input.id}`, input.terms),
  );
}

/** Asks for a change of the brand's accepted or lapsed request, for the admin team to review. */
export function useAskChange() {
  return useRequestAction((input: { id: number; terms: CampaignRequestInput; reason: string }) =>
    api.post<CampaignRequest>(`/api/campaign-requests/${input.id}/changes`, {
      terms: input.terms,
      reason: input.reason,
    }),
  );
}

/** Withdraws the brand's change waiting for review. */
export function useWithdrawChange() {
  return useRequestAction((input: { id: number; changeId: number }) =>
    api.post<CampaignRequest>(
      `/api/campaign-requests/${input.id}/changes/${input.changeId}/withdraw`,
    ),
  );
}

/** Offers the brand's request, not yet funded, to another wallet. */
export function useOfferRequest() {
  return useRequestAction((input: { id: number; to: string }) =>
    api.post<CampaignRequest>(`/api/campaign-requests/${input.id}/transfers`, { to: input.to }),
  );
}

/** Cancels the brand's move not yet taken over. */
export function useCancelMove() {
  return useRequestAction((input: { id: number; transferId: number }) =>
    api.post<CampaignRequest>(
      `/api/campaign-requests/${input.id}/transfers/${input.transferId}/cancel`,
    ),
  );
}

/** Takes over a request offered to the signed-in wallet. */
export function useTakeOverRequest() {
  return useRequestAction((input: { id: number; transferId: number }) =>
    api.post<CampaignRequest>(
      `/api/campaign-requests/${input.id}/transfers/${input.transferId}/accept`,
    ),
  );
}

/** Declines a request offered to the signed-in wallet. */
export function useDeclineRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number; transferId: number }) =>
      api.post<void>(`/api/campaign-requests/${input.id}/transfers/${input.transferId}/decline`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }),
  });
}

/**
 * Accepts a submitted request into a manifest, with an optional note for the brand (admins).
 * `updatedAt` is the request's as the admin saw it. The API refuses a request edited since.
 */
export function useAcceptRequest() {
  return useRequestAction((input: { id: number; note: string; updatedAt: string }) =>
    api.post<CampaignRequest>(`/api/admin/campaign-requests/${input.id}/accept`, {
      ...(input.note ? { note: input.note } : {}),
      updatedAt: input.updatedAt,
    }),
  );
}

/** Rejects a submitted request with a note for the brand (admins). See `useAcceptRequest`. */
export function useRejectRequest() {
  return useRequestAction((input: { id: number; note: string; updatedAt: string }) =>
    api.post<CampaignRequest>(`/api/admin/campaign-requests/${input.id}/reject`, {
      note: input.note,
      updatedAt: input.updatedAt,
    }),
  );
}

/** Accepts a change, which fixes its terms in a new manifest (admins). */
export function useAcceptChange() {
  return useRequestAction((input: { id: number; changeId: number; note: string }) =>
    api.post<CampaignRequest>(
      `/api/admin/campaign-requests/${input.id}/changes/${input.changeId}/accept`,
      input.note ? { note: input.note } : {},
    ),
  );
}

/** Rejects a change with a note for the brand. The terms stay (admins). */
export function useRejectChange() {
  return useRequestAction((input: { id: number; changeId: number; note: string }) =>
    api.post<CampaignRequest>(
      `/api/admin/campaign-requests/${input.id}/changes/${input.changeId}/reject`,
      { note: input.note },
    ),
  );
}
